import type { Direction, GameState, TileType } from "../domain/types";
import { tileBalance } from "../content/Content";
import { SeededRandom } from "../infrastructure/rng/SeededRandom";

export type MoveResult = {
  valid: boolean;
  message: string;
  event?: "treasure" | "trap" | "win" | "loss" | "curse" | "secretRoom";
};

export class GameEngine {
  constructor(public state: GameState) {
    this.revealTile(state.playerIndex);
  }

  move(direction: Direction): MoveResult {
    if (this.state.status !== "playing") return { valid: false, message: "The expedition has ended." };
    const offset = { up: -this.state.size, down: this.state.size, left: -1, right: 1 }[direction];
    const target = this.state.playerIndex + offset;
    if (!this.canMove(target, direction)) return { valid: false, message: "That route is blocked." };
    this.state.playerIndex = target;
    this.state.movesRemaining--;
    const curseBeforeMove = this.state.activeCurse?.movesRemaining;
    this.state.combo.window--;
    if (this.state.combo.window < 0) this.state.combo.current = 1;
    this.revealTile(target);
    const result = this.resolve(target);
    if (curseBeforeMove !== undefined && this.state.activeCurse) this.advanceCurse();
    if (this.state.hp <= 0 || this.state.movesRemaining <= 0 || this.state.timer.remainingSeconds <= 0) {
      this.state.status = "lost";
      return {
        valid: true,
        message: this.state.hp <= 0 ? "Your explorer fell."
          : this.state.timer.remainingSeconds <= 0 ? "Time ran out." : "No moves remain.",
        event: "loss",
      };
    }
    if (this.state.status === "playing" && !this.hasValidMove()) {
      this.state.status = "lost";
      return { valid: true, message: "No valid moves remain.", event: "loss" };
    }
    return result;
  }

  pause(): boolean {
    if (this.state.status !== "playing") return false;
    this.state.status = "paused";
    return true;
  }

  resume(): boolean {
    if (this.state.status !== "paused") return false;
    this.state.status = "playing";
    return true;
  }

  restart(state: GameState): void {
    this.state = state;
    this.revealTile(state.playerIndex);
  }

  inspect(direction: Direction): string {
    const offset = { up: -this.state.size, down: this.state.size, left: -1, right: 1 }[direction];
    const target = this.state.playerIndex + offset;
    if (!this.canMove(target, direction)) return "The passage is sealed.";
    const tile = this.state.grid[target];
    if (tile.discoveryState !== "unknown") return "This chamber has already been explored.";
    return {
      ancient_energy: "Ancient energy detected",
      possible_relic: "Possible relic nearby",
      cursed_chamber: "Cursed chamber",
      none: "The chamber is silent",
    }[tile.omen];
  }

  isRisky(direction: Direction): boolean {
    const offset = { up: -this.state.size, down: this.state.size, left: -1, right: 1 }[direction];
    const target = this.state.playerIndex + offset;
    return this.canMove(target, direction) &&
      this.state.grid[target].discoveryState === "unknown" &&
      this.state.grid[target].omen !== "none";
  }

  tick(seconds = 1): MoveResult {
    if (this.state.status !== "playing") return { valid: false, message: "Timer is not running." };
    this.state.timer.remainingSeconds = Math.max(0, this.state.timer.remainingSeconds - seconds);
    if (this.state.timer.remainingSeconds === 0) {
      this.state.status = "lost";
      return { valid: true, message: "Time ran out.", event: "loss" };
    }
    return { valid: true, message: `${this.state.timer.remainingSeconds} seconds remain.` };
  }

  private canMove(target: number, direction: Direction): boolean {
    if (target < 0 || target >= this.state.grid.length) return false;
    if ((direction === "left" || direction === "right") &&
      Math.floor(target / this.state.size) !== Math.floor(this.state.playerIndex / this.state.size)) return false;
    return this.state.grid[target].walkable;
  }

  private hasValidMove(): boolean {
    return (["up", "down", "left", "right"] as Direction[]).some(direction => {
      const offset = { up: -this.state.size, down: this.state.size, left: -1, right: 1 }[direction];
      return this.canMove(this.state.playerIndex + offset, direction);
    });
  }

  private objectivesComplete(): boolean {
    return this.state.treasuresCollected >= this.state.treasureGoal &&
      this.state.rewards.relicFragments >= this.state.objective.relicFragmentsRequired &&
      (this.state.lastSecretRoom ? 1 : 0) >= this.state.objective.secretRoomsRequired;
  }

  private objectiveProgressMessage(): string {
    const missing: string[] = [];
    const treasure = this.state.treasureGoal - this.state.treasuresCollected;
    const fragments = this.state.objective.relicFragmentsRequired - this.state.rewards.relicFragments;
    const secrets = this.state.objective.secretRoomsRequired - (this.state.lastSecretRoom ? 1 : 0);
    if (treasure > 0) missing.push(`${treasure} more treasure`);
    if (fragments > 0) missing.push(`${fragments} relic fragment`);
    if (secrets > 0) missing.push("the secret room");
    return `The exit still requires ${missing.join(" and ")}.`;
  }

  private resolve(index: number): MoveResult {
    const cell = this.state.grid[index];
    cell.visited = true;
    if (cell.type === "exit") {
      if (this.objectivesComplete()) {
        this.state.status = "won";
        return { valid: true, message: "The vault is open.", event: "win" };
      }
      return { valid: true, message: this.objectiveProgressMessage() };
    }
    if (["coin", "treasure", "gem", "relic", "relicFragment"].includes(cell.type)) return this.collect(cell.type, index);
    if (cell.type === "trap" || cell.type === "poison") {
      const balance = tileBalance[cell.type];
      const damage = (balance.damage ?? 0) * this.state.fragileTrapMultiplier;
      this.state.hp -= damage;
      this.state.fragileTrapMultiplier = 1;
      if (this.state.activeCurse?.type === "fragile") this.clearCurse();
      this.state.statusEffect = cell.type === "poison" ? "poisoned" : "none";
      this.state.trapsTriggered++;
      this.state.combo.current = 1;
      this.state.movesRemaining -= balance.movePenalty ?? 0;
      this.state.lastDamageFeedback = { tileIndex: index, amount: damage };
      this.consume(cell);
      return { valid: true, message: `${damage} damage from a hidden trap.`, event: "trap" };
    }
    if (cell.type === "freeze") {
      const penalty = tileBalance.freeze.movePenalty ?? 0;
      this.state.movesRemaining -= penalty;
      this.state.statusEffect = "frozen";
      this.consume(cell);
      return { valid: true, message: `Frozen rune: ${penalty} moves lost.` };
    }
    if (cell.type === "multiplier") {
      this.state.multiplierMoves = 5;
      this.state.statusEffect = "fortune";
      this.consume(cell);
      return { valid: true, message: "Fortune doubles treasure for 5 moves." };
    }
    if (cell.type === "portal") this.teleport(cell);
    if (cell.type === "curse") {
      const message = this.activateCurse(index);
      this.consume(cell);
      return { valid: true, message, event: "curse" };
    }
    if (cell.type === "secretClue") {
      this.state.score += tileBalance.secretClue.score;
      this.state.rewards.secretClues++;
      this.consume(cell);
      return { valid: true, message: "Secret room clue discovered. A hidden route is marked.", event: "treasure" };
    }
    if (cell.type === "mystery") {
      const secretChance = this.state.levelId === "tutorial-5" ||
        new SeededRandom(this.state.seed ^ index ^ this.state.movesRemaining).next() < 0.03;
      if (secretChance) {
        const message = this.discoverSecretRoom(index);
        this.consume(cell);
        return { valid: true, message, event: "secretRoom" };
      }
      const outcomes: TileType[] = ["coin", "empty", "trap", "secretClue"];
      cell.type = new SeededRandom(this.state.seed ^ index ^ 0x51f15e).pick(outcomes);
      return this.resolve(index);
    }
    if (this.state.multiplierMoves > 0) this.state.multiplierMoves--;
    if (this.state.multiplierMoves === 0 && this.state.statusEffect === "fortune") this.state.statusEffect = "none";
    return { valid: true, message: cell.type === "portal" ? "The portal folds the grid." : "Empty chamber. Only dust remains." };
  }

  private collect(type: TileType, index: number): MoveResult {
    const reward = tileBalance[type];
    this.state.combo.current = this.state.combo.window >= 0 ? Math.min(5, this.state.combo.current + 0.5) : 1;
    this.state.combo.window = 2;
    this.state.combo.best = Math.max(this.state.combo.best, this.state.combo.current);
    const temporary = this.state.multiplierMoves > 0 ? 2 : 1;
    const curseFactor = this.state.activeCurse?.type === "greed" ? 0.5 : 1;
    const gained = Math.floor(reward.score * this.state.combo.current * temporary * curseFactor);
    this.state.score += gained;
    this.state.treasuresCollected++;
    this.state.rewards.gold += reward.gold ?? 0;
    this.state.rewards.gems += reward.gems ?? 0;
    this.state.rewards.relics += reward.relics ?? 0;
    if (type === "relicFragment") this.state.rewards.relicFragments++;
    this.state.lastTreasureFeedback = {
      tileIndex: index,
      rewardType: type,
      score: gained,
      gold: reward.gold ?? 0,
      combo: this.state.combo.current,
      bigReward: ["gem", "relic", "relicFragment"].includes(type) || gained >= 250,
    };
    this.consume(this.state.grid[index]);
    return { valid: true, message: `Treasure secured: +${gained} score.`, event: "treasure" };
  }

  private teleport(cell: GameState["grid"][number]): void {
    this.consume(cell);
    const choices = this.state.grid.map((tile, index) => ({ tile, index }))
      .filter(candidate => candidate.tile.walkable && candidate.index !== this.state.playerIndex);
    const rng = new SeededRandom(this.state.seed ^ this.state.movesRemaining);
    this.state.playerIndex = rng.pick(choices).index;
    this.revealTile(this.state.playerIndex);
  }

  private revealTile(index: number): void {
    const tile = this.state.grid[index];
    tile.revealed = true;
    if (tile.discoveryState === "unknown") tile.discoveryState = "revealed";
  }

  private consume(tile: GameState["grid"][number]): void {
    tile.consumed = true;
    tile.discoveryState = "consumed";
    tile.type = "empty";
  }

  private activateCurse(index: number): string {
    const curse = new SeededRandom(this.state.seed ^ index ^ this.state.movesRemaining)
      .pick(["greed", "shadow", "time", "fragile"] as const);
    this.state.statusEffect = "cursed";
    this.state.activeCurse = { type: curse, movesRemaining: curse === "fragile" ? 1 : 5 };
    let message = "";
    if (curse === "greed") message = "CURSE ACTIVATED: Greed Curse — rewards reduced for 5 moves";
    if (curse === "shadow") {
      this.hideNearbyTiles();
      message = "CURSE ACTIVATED: Shadow Curse — nearby chambers are hidden again";
    }
    if (curse === "time") {
      this.state.timer.remainingSeconds = Math.max(0, this.state.timer.remainingSeconds - 10);
      message = "CURSE ACTIVATED: Time Curse — 10 seconds lost";
    }
    if (curse === "fragile") {
      this.state.fragileTrapMultiplier = 2;
      message = "CURSE ACTIVATED: Fragile Curse — next trap deals double damage";
    }
    this.state.lastCurseMessage = message;
    return message;
  }

  private hideNearbyTiles(): void {
    const row = Math.floor(this.state.playerIndex / this.state.size);
    const column = this.state.playerIndex % this.state.size;
    for (const [dy, dx] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
      const y = row + dy, x = column + dx;
      if (y < 0 || y >= this.state.size || x < 0 || x >= this.state.size) continue;
      const tile = this.state.grid[y * this.state.size + x];
      if (!tile.consumed && tile.type !== "exit") {
        tile.discoveryState = "unknown";
        tile.revealed = false;
      }
    }
  }

  private advanceCurse(): void {
    if (!this.state.activeCurse) return;
    this.state.activeCurse.movesRemaining--;
    if (this.state.activeCurse.movesRemaining <= 0) this.clearCurse();
  }

  private clearCurse(): void {
    this.state.activeCurse = undefined;
    this.state.statusEffect = "none";
    this.state.fragileTrapMultiplier = 1;
  }

  private discoverSecretRoom(index: number): string {
    const reward = new SeededRandom(this.state.seed ^ index ^ 0x5ec2e7)
      .pick(["gold_chest", "relic_fragment", "combo_boost"] as const);
    let amount = 0;
    if (reward === "gold_chest") {
      amount = 150;
      this.state.rewards.gold += amount;
      this.state.score += amount;
    } else if (reward === "relic_fragment") {
      amount = 1;
      this.state.rewards.relicFragments += amount;
      this.state.treasuresCollected++;
    } else {
      amount = 2;
      this.state.combo.current = Math.min(5, this.state.combo.current + amount);
      this.state.combo.best = Math.max(this.state.combo.best, this.state.combo.current);
    }
    this.state.lastSecretRoom = { reward, amount };
    return "SECRET ROOM DISCOVERED";
  }
}
